import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
`;

const Section = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.15rem;
  color: #333;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #dee2e6;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.25rem;
`;

const Card = styled.div`
  border: 1px solid ${props => props.on ? '#b7e1c1' : '#e3e6ea'};
  border-radius: 8px;
  padding: 1.25rem;
  background: ${props => props.on ? '#f6fcf8' : '#fff'};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CardTitle = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.4rem;
  font-size: 0.875rem;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    background: #f5f5f5;
    color: #999;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  background: #fff;
`;

const Hint = styled.div`
  font-size: 0.78rem;
  color: #888;
  margin-top: 0.35rem;
  line-height: 1.5;
`;

const SwitchRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #555;
  cursor: pointer;
  user-select: none;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 4px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' && `
    background-color: #007bff;
    color: #fff;
    &:hover { background-color: #0069d9; }
  `}

  ${props => props.variant === 'secondary' && `
    background-color: #6c757d;
    color: #fff;
    &:hover { background-color: #5a6268; }
  `}

  ${props => props.variant === 'outline' && `
    background-color: #fff;
    color: #007bff;
    border: 1px solid #007bff;
    &:hover { background-color: #f0f7ff; }
  `}

  &:disabled {
    background-color: #adb5bd;
    cursor: not-allowed;
    border-color: #adb5bd;
    color: #fff;
  }
`;

const Alert = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : '#f5c6cb'};
  background-color: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
`;

const ResultTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th, td {
    padding: 0.6rem;
    border-bottom: 1px solid #eee;
    text-align: left;
  }

  th {
    color: #666;
    font-weight: 600;
    background: #fafafa;
  }
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  background: ${props => props.ok ? '#d4edda' : '#f8d7da'};
  color: ${props => props.ok ? '#155724' : '#721c24'};
`;

const CodeArea = styled.textarea`
  width: 100%;
  min-height: 320px;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 0.8rem;
  line-height: 1.5;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f8f9fa;
  color: #333;
  resize: vertical;
`;

const emptyConfig = {
  enabled: false,
  provider: 'custom',
  origin: 'https://www.hfht-info.com',
  pc: { enabled: false, domain: '', assetPrefix: '', cacheDays: 30 },
  wap: { enabled: false, domain: '', assetPrefix: '', cacheDays: 30 },
  miniprogram: { enabled: false, domain: '', cacheDays: 30 },
  fileTypes: 'js,css,png,jpg,jpeg,gif,webp,svg,ico,woff,woff2,ttf,mp4,pdf,doc,docx,xls,xlsx,zip',
  httpsOnly: true
};

const SCENES = [
  { key: 'pc', title: 'PC 端官网', desc: '桌面版网站，静态资源与上传文件走 CDN 域名' },
  { key: 'wap', title: 'WAP 端（手机网页）', desc: '手机浏览器访问的网页版，通常与 PC 共用同一套构建产物' },
  { key: 'miniprogram', title: '微信小程序', desc: '小程序内加载的远程图片等资源，保存后 60 秒内自动下发生效' }
];

const CdnSettingsPage = () => {
  const [config, setConfig] = useState(emptyConfig);
  const [nginx, setNginx] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState(null);
  const [message, setMessage] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/cdn');
      if (res.data && res.data.success) {
        const { nginxSnippet, ...rest } = res.data.data;
        setConfig({ ...emptyConfig, ...rest });
        setNginx(nginxSnippet || '');
      }
    } catch (error) {
      setMessage({ type: 'error', text: '读取配置失败：' + (error.response?.data?.message || error.message) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const updateScene = (key, patch) => {
    setConfig(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);
      const res = await axios.put('/api/admin/cdn', config);
      if (res.data && res.data.success) {
        setNginx(res.data.data.nginxSnippet || '');
        setMessage({ type: 'success', text: 'CDN 配置已保存，小程序端将在 60 秒内生效' });
      }
    } catch (error) {
      const data = error.response?.data;
      const text = data?.errors?.length
        ? data.errors.join('；')
        : (data?.message || error.message);
      setMessage({ type: 'error', text: '保存失败：' + text });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setResults(null);
      setMessage(null);
      // 传入当前表单配置，未保存也能自检
      const res = await axios.post('/api/admin/cdn/test', { config });
      if (res.data && res.data.success) setResults(res.data.data);
    } catch (error) {
      setMessage({ type: 'error', text: '自检失败：' + (error.response?.data?.message || error.message) });
    } finally {
      setTesting(false);
    }
  };

  const copyNginx = () => {
    if (!nginx) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(nginx)
        .then(() => setMessage({ type: 'success', text: 'Nginx 配置已复制到剪贴板' }))
        .catch(() => setMessage({ type: 'error', text: '复制失败，请手动选中复制' }));
    } else {
      setMessage({ type: 'error', text: '当前浏览器不支持自动复制，请手动选中复制' });
    }
  };

  return (
    <AdminLayout title="CDN 加速">
      <Container>
        <Header>
          <Title>CDN 加速配置</Title>
          <ButtonGroup>
            <Button variant="outline" onClick={handleTest} disabled={testing}>
              {testing ? '自检中…' : '连通性自检'}
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? '保存中…' : '保存配置'}
            </Button>
          </ButtonGroup>
        </Header>

        {message && <Alert type={message.type}>{message.text}</Alert>}

        {loading ? (
          <Section>加载中…</Section>
        ) : (
          <>
            <Section>
              <SectionTitle>全局设置</SectionTitle>
              <Grid>
                <FormGroup>
                  <SwitchRow>
                    <input
                      type="checkbox"
                      checked={config.enabled}
                      onChange={e => setConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                    />
                    启用 CDN 加速总开关
                  </SwitchRow>
                  <Hint>关闭后三端全部直连源站，已填写的域名不会被清除。</Hint>
                </FormGroup>

                <FormGroup>
                  <Label>服务商</Label>
                  <Select
                    value={config.provider}
                    onChange={e => setConfig(prev => ({ ...prev, provider: e.target.value }))}
                  >
                    <option value="aliyun">阿里云 CDN</option>
                    <option value="tencent">腾讯云 CDN</option>
                    <option value="qiniu">七牛云 CDN</option>
                    <option value="cloudflare">Cloudflare</option>
                    <option value="custom">自建 / 其他</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>源站地址（回源地址）</Label>
                  <Input
                    value={config.origin}
                    placeholder="https://www.hfht-info.com"
                    onChange={e => setConfig(prev => ({ ...prev, origin: e.target.value }))}
                  />
                  <Hint>CDN 节点回源时访问的站点，需与 CDN 控制台「回源配置」保持一致。</Hint>
                </FormGroup>

                <FormGroup>
                  <Label>加速文件类型</Label>
                  <Input
                    value={config.fileTypes}
                    onChange={e => setConfig(prev => ({ ...prev, fileTypes: e.target.value }))}
                  />
                  <Hint>英文逗号分隔，用于生成 Nginx 缓存规则与 CDN 缓存策略。</Hint>
                </FormGroup>

                <FormGroup>
                  <SwitchRow>
                    <input
                      type="checkbox"
                      checked={config.httpsOnly}
                      onChange={e => setConfig(prev => ({ ...prev, httpsOnly: e.target.checked }))}
                    />
                    仅允许 HTTPS 域名
                  </SwitchRow>
                  <Hint>建议开启；小程序端强制要求 HTTPS。</Hint>
                </FormGroup>
              </Grid>
            </Section>

            <Section>
              <SectionTitle>分端配置</SectionTitle>
              <Grid>
                {SCENES.map(scene => {
                  const s = config[scene.key];
                  return (
                    <Card key={scene.key} on={s.enabled}>
                      <CardHeader>
                        <CardTitle>{scene.title}</CardTitle>
                        <SwitchRow>
                          <input
                            type="checkbox"
                            checked={s.enabled}
                            onChange={e => updateScene(scene.key, { enabled: e.target.checked })}
                          />
                          {s.enabled ? '已开启' : '未开启'}
                        </SwitchRow>
                      </CardHeader>

                      <FormGroup>
                        <Label>CDN 域名</Label>
                        <Input
                          value={s.domain}
                          placeholder="https://cdn.example.com"
                          disabled={!s.enabled}
                          onChange={e => updateScene(scene.key, { domain: e.target.value })}
                        />
                        <Hint>{scene.desc}</Hint>
                      </FormGroup>

                      {scene.key !== 'miniprogram' && (
                        <FormGroup>
                          <Label>静态资源前缀（可选）</Label>
                          <Input
                            value={s.assetPrefix}
                            placeholder="留空则使用 CDN 域名根路径"
                            disabled={!s.enabled}
                            onChange={e => updateScene(scene.key, { assetPrefix: e.target.value })}
                          />
                          <Hint>
                            若构建产物托管在对象存储（如 OSS/COS），可填 <code>PUBLIC_URL</code> 对应的前缀，
                            例如 <code>https://cdn.example.com/web</code>。
                          </Hint>
                        </FormGroup>
                      )}

                      <FormGroup>
                        <Label>缓存时长（天）</Label>
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          value={s.cacheDays}
                          disabled={!s.enabled}
                          onChange={e => updateScene(scene.key, { cacheDays: e.target.value })}
                        />
                      </FormGroup>
                    </Card>
                  );
                })}
              </Grid>
            </Section>

            {results && (
              <Section>
                <SectionTitle>自检结果</SectionTitle>
                <ResultTable>
                  <thead>
                    <tr>
                      <th>端</th>
                      <th>域名</th>
                      <th>状态</th>
                      <th>HTTP</th>
                      <th>耗时</th>
                      <th>解析 IP</th>
                      <th>证书</th>
                      <th>CDN 标识</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(r => (
                      <tr key={r.scene}>
                        <td>{r.name}</td>
                        <td>{r.url}</td>
                        <td><Badge ok={r.ok}>{r.ok ? '正常' : (r.certMatch === false ? '证书不匹配' : '异常')}</Badge></td>
                        <td>{r.statusCode || '-'}</td>
                        <td>{r.elapsed ? r.elapsed + ' ms' : '-'}</td>
                        <td>{r.dns && r.dns.ip ? r.dns.ip : '-'}</td>
                        <td>
                          {r.cert
                            ? (
                              <>
                                {r.cert.cn || '-'}
                                {r.certMatch === false && (
                                  <div style={{ color: '#721c24', fontSize: '0.75rem' }}>证书不匹配</div>
                                )}
                              </>
                            )
                            : (r.certMatch === null ? '-' : '无证书')}
                        </td>
                        <td>{r.headers ? ([r.headers.via, r.headers.cache].filter(Boolean).join(' / ') || '-') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </ResultTable>
                {/* 异常端的完整诊断：表格列窄，长文案单独列出 */}
                {results.some((r) => !r.ok && r.message) && (
                  <Section style={{ borderLeft: '4px solid #dc3545', background: '#fff5f5' }}>
                    <SectionTitle>诊断与处理建议</SectionTitle>
                    {results.filter((r) => !r.ok && r.message).map((r) => (
                      <div key={r.scene} style={{ marginBottom: '0.75rem', fontSize: '0.875rem', lineHeight: 1.7 }}>
                        <strong>{r.name}（{r.url}）</strong>
                        <div style={{ color: '#721c24' }}>{r.message}</div>
                        {r.cert && r.cert.cn && (
                          <div style={{ color: 'var(--gray-600)', fontSize: '0.8rem' }}>
                            节点证书 CN：{r.cert.cn}
                            {r.cert.validTo ? `（有效期至 ${new Date(r.cert.validTo).toLocaleDateString('zh-CN')}）` : ''}
                          </div>
                        )}
                      </div>
                    ))}
                  </Section>
                )}
                <Hint>
                  提示：自检从服务器发起，验证域名可达性、响应头与证书是否覆盖该域名。
                  若「证书不匹配」，常见两种原因：
                  ① 加速域名上还没部署证书 —— 到 CDN 控制台为该域名上传证书（CN/SAN 必须包含该域名）；
                  ② 填的是 CDN 分配的 CNAME 接入地址（如 xxx.a.bdydns.com）—— 它只能作为 DNS 的 CNAME 目标，加速域名要填自己的域名（如 cdn.hfht-info.com）。
                  真实加速效果需在 CDN 控制台查看命中率与带宽。
                </Hint>
              </Section>
            )}

            <Section>
              <SectionTitle>源站 Nginx 配置（自动生成）</SectionTitle>
              <CodeArea value={nginx} readOnly />
              <ButtonGroup style={{ marginTop: '1rem' }}>
                <Button variant="secondary" onClick={copyNginx}>复制配置</Button>
              </ButtonGroup>
              <Hint>
                把上面的片段加入源站 Nginx 并 reload；CDN 控制台的「回源地址」填写源站域名，缓存策略与上表保持一致。
              </Hint>
            </Section>
          </>
        )}
      </Container>
    </AdminLayout>
  );
};

export default CdnSettingsPage;
